/**
 * PersistentStats — localStorage-backed lifetime player statistics.
 *
 * Tracks cumulative stats across runs: total runs, best clear time,
 * creatures unburdened, loot collected, highest floor, Guardians
 * unburdened. No account system — data is per-browser via localStorage.
 *
 * Field names below (`totalEnemiesKilled`, `bossesDefeated`, param
 * `bossKilled`) are kept in v1 form because they are persisted into the
 * localStorage JSON blob — a rename would orphan every existing player's
 * saved stats. User-facing labels live in `locale.ts` under
 * `hud.stats.*` (e.g. `hud.stats.enemies_killed` displays as "Unburdened").
 */

import type { ExtractionResults } from '@game/shared';

const STORAGE_KEY = 'dungeon_persistent_stats';

export interface PlayerStats {
  totalRunsCompleted: number;
  totalRunsAttempted: number;
  bestClearTimeMs: number; // 0 = no clear yet
  totalEnemiesKilled: number;
  totalLootCollected: number;
  highestFloorReached: number;
  bossesDefeated: number;
}

const DEFAULT_STATS: PlayerStats = {
  totalRunsCompleted: 0,
  totalRunsAttempted: 0,
  bestClearTimeMs: 0,
  totalEnemiesKilled: 0,
  totalLootCollected: 0,
  highestFloorReached: 0,
  bossesDefeated: 0,
};

export function loadStats(): PlayerStats {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATS, ...parsed };
  } catch {
    return { ...DEFAULT_STATS };
  }
}

export function saveStats(stats: PlayerStats): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/** Record that a new run was started (increments totalRunsAttempted) */
export function recordRunStart(): void {
  const stats = loadStats();
  stats.totalRunsAttempted++;
  saveStats(stats);
}

/** Record a completed extraction run, updating all relevant lifetime stats */
export function recordRunComplete(
  results: ExtractionResults,
  floorReached: number,
  bossKilled: boolean,
): void {
  const stats = loadStats();
  stats.totalRunsCompleted++;
  stats.totalEnemiesKilled += results.enemiesKilled;
  stats.totalLootCollected += results.totalLootCount;

  if (floorReached > stats.highestFloorReached) {
    stats.highestFloorReached = floorReached;
  }

  if (bossKilled) {
    stats.bossesDefeated++;
  }

  // Best clear time: only update if we have a survival time and it's better (or first clear)
  if (results.survivalTimeMs > 0) {
    if (stats.bestClearTimeMs === 0 || results.survivalTimeMs < stats.bestClearTimeMs) {
      stats.bestClearTimeMs = results.survivalTimeMs;
    }
  }

  saveStats(stats);
}
